<template>
  <v-card class="my-2">
    <!-- Clickable Title for Expand/Collapse -->
    <v-card-title @click="isTableExpanded = !isTableExpanded" style="cursor: pointer; user-select: none;">
      {{ project.job_number }} - {{ project.country }} - {{ project.decipher_path }}
      <v-spacer></v-spacer>

      <!-- Action Buttons Wrapper (Click propagation stopped so it doesn't trigger collapse) -->
      <div @click.stop class="d-flex align-center">
        <v-btn small color="success mr-2"
          :disabled="selected.length < 2"
          :loading="comparisonLoading"
          @click="$emit('compare-request', selected)"
        >
          Compare ({{ selected.length }})
        </v-btn>
        <v-badge
          :content="filterCount"
          :value="filterCount"
          color="success"
          overlap
          offset-x="15"
          offset-y="15"
        >
          <v-btn icon @click="openFilterDialog" >
            <v-icon>{{ icons.mdiFilter }}</v-icon>
          </v-btn>
        </v-badge>

        <v-menu bottom left>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon v-bind="attrs" v-on="on">
                <v-icon>{{ icons.mdiDotsVertical }}</v-icon>
              </v-btn>
            </template>

            <v-list>
              <v-list-item @click="exportKPI">
                <v-list-item-title>
                  <v-icon size="20" class="me-2">{{ icons.mdiFileExcelOutline }}</v-icon>
                  <span>Download Results</span>
                </v-list-item-title>
              </v-list-item>
              <v-list-item @click="$emit('update-ef')">
                <v-list-item-title>
                  <v-icon size="20" class="me-2">{{ icons.mdiProgressUpload }}</v-icon>
                  <span>Re-upload EF data</span>
                </v-list-item-title>
              </v-list-item>
              <v-list-item @click="triggerUpload">
                <v-list-item-title>
                  <v-icon size="20" class="me-2">{{ icons.mdiCloudUpload }}</v-icon>
                  <span>Upload Manual Image</span>
                </v-list-item-title>
                
                <input 
                  type="file" 
                  ref="mediaFileInput" 
                  style="display: none" 
                  accept="image/*" 
                  @change="uploadMediaFile"
                />
              </v-list-item>
            </v-list>
            <input
              ref="fixationsFileInput"
              type="file"
              accept=".csv, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              style="display: none"
              @change="uploadFixationsFile"
            />
          </v-menu>
      </div>

      <!-- Expand/Collapse Indicator -->
      <v-icon class="ml-2">{{ isTableExpanded ? icons.mdiChevronUp : icons.mdiChevronDown }}</v-icon>
    </v-card-title>
    
    <!-- Collapsible Table Area -->
    <v-expand-transition>
      <div v-show="isTableExpanded">
        <v-card-text>
          <v-data-table
            v-model="selected"
            :headers="headers"
            :items="medias"
            @click:row="handleRowClick"
            item-key="id"
            show-select
            :item-class="rowClass"
            dense
            :loading="loading"
          >
            <template v-slot:item.type="{ item }">
              <span>{{ typeText(item.type) }}</span>
            </template>
            <template v-slot:item.source="{ item }">
              <span>{{ sourceText(item.source) }}</span>
            </template>
            <template v-slot:item.shelf="{ item }">
              <v-simple-checkbox v-model="item.shelf" disabled></v-simple-checkbox>
            </template>
            <template v-slot:item.pack="{ item }">
              <v-simple-checkbox v-model="item.pack" disabled></v-simple-checkbox>
            </template>
            <template v-slot:item.aggregated="{ item }">
              <v-simple-checkbox v-model="item.aggregated" disabled></v-simple-checkbox>
            </template>

            <!-- actions -->
            <template #[`item.actions`]="{item}">
              <div @click.stop>
                <v-menu bottom left>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon v-bind="attrs" v-on="on">
                      <v-icon>{{ icons.mdiDotsVertical }}</v-icon>
                    </v-btn>
                  </template>

                  <v-list>
                    <v-list-item @click="openEditMediaDialog(item)">
                      <v-list-item-title>
                        <v-icon size="20" class="me-2">{{ icons.mdiPencil }}</v-icon>
                        <span>Edit Image</span>
                      </v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="triggerFixationsFileUpload(item)">
                      <v-list-item-title>
                        <v-icon size="20" class="me-2">{{ icons.mdiTableHeadersEye }}</v-icon>
                        <span>Upload Fixations</span>
                      </v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="openDeleteConfirmDialog(item)">
                      <v-list-item-title>
                        <v-icon size="20" class="me-2">{{ icons.mdiTableHeadersEyeOff }}</v-icon>
                        <span>Delete Fixations</span>
                      </v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="openMediaDeleteConfirmDialog(item)">
                      <v-list-item-title>
                        <v-icon size="20" class="me-2">{{ icons.mdiImageOffOutline }}</v-icon>
                        <span>Delete Images/AOIs/Fixations</span>
                      </v-list-item-title>
                    </v-list-item>
                  </v-list>
                  <input
                    ref="fixationsFileInput"
                    type="file"
                    accept=".csv, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    style="display: none"
                    @change="uploadFixationsFile"
                  />
                </v-menu>
              </div>
            </template>
          </v-data-table>
        </v-card-text>
      </div>
    </v-expand-transition>

    <oculis-filter
      v-model="showFilterDialog"
      :project="project"
      @filter-applied="updateFilter"
    >

    </oculis-filter>

    

    <!-- Existing Dialogs -->
    <v-dialog v-model="showDeleteConfirmDialog" max-width="400px">
      <v-card>
          <v-card-title>Delete Fixations</v-card-title>
          <v-card-text>Are you sure you want to delete all the fixations? This action cannot be undone.</v-card-text>
          <v-card-actions>
              <v-spacer></v-spacer> 
              <v-btn text @click="cancelDeletion">Cancel</v-btn>
              <v-btn color="error" @click="clearFixations" :loading="fixationsLoading">Delete</v-btn>
          </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showMediaDeleteConfirmDialog" max-width="400px">
      <v-card>
          <v-card-title>Delete Image/AOIs/Fixations</v-card-title>
          <v-card-text>Are you sure you want to delete everything? This action cannot be undone.</v-card-text>
          <v-card-actions>
              <v-spacer></v-spacer> 
              <v-btn text @click="cancelMediaDeletion">Cancel</v-btn>
              <v-btn color="error" @click="clearMedia" :loading="mediasLoading">Delete</v-btn>
          </v-card-actions>
      </v-card>
    </v-dialog>
  
    <v-dialog v-model="showEditLabelDialog" max-width="400px">
        <v-card>
            <v-card-title>Edit Media</v-card-title>
            <v-card-text>
                <v-text-field v-model="editedMediaLabel" label="Name" outlined dense autofocus hide-details @keydown.enter="saveEditedMedia" class="mb-2"></v-text-field>
                <v-select v-model="editedMediaType" label="Type" :items="typeItems" outlined dense hide-details @keydown.enter="saveEditedMedia" class="mb-2"></v-select>
                <v-select v-model="editedMediaSource" label="Source" :items="sourceItems" outlined dense hide-details @keydown.enter="saveEditedMedia" class="mb-2"></v-select>
            </v-card-text>
            <v-card-actions>
                <v-btn text @click="cancelEditMedia">Cancel</v-btn>
                <v-btn color="primary" @click="saveEditedMedia" :loading="mediaLoading">Save</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
    
  </v-card>
</template>

<script>
import { ref, watch, computed, onMounted } from '@vue/composition-api'
import useOculis from '../../useOculis'
import { 
  mdiCloudUpload,
  mdiProgressUpload,
  mdiDotsVertical,
  mdiImageOffOutline,
  mdiTableHeadersEye,
  mdiPencil,
  mdiTableHeadersEyeOff,
  mdiFilter,
  mdiFileExcelOutline,
  mdiChevronUp,
  mdiChevronDown,
} from '@mdi/js'
import OculisFilter from '../oculis-filter/OculisFilter.vue';

export default {
  components: {
    OculisFilter
  },
  props: {
    project: { type: Object, default: () => ({}) },
    medias: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    comparisonLoading: { type: Boolean, default: false },
    fixationsLoading: { type: Boolean, default: false },
    mediasLoading: { type: Boolean, default: false },
  },
  setup(props, { emit }) {
    const { saveFilter, downloadKPIAnalysis, selectedItem,  
    } = useOculis()


    const selected = ref([])
    const activeItem = ref(null)
    const uploading = ref(false)
    const mediaFileInput = ref(null)
    const fixationsFileInput = ref(null)
    const isTableExpanded = ref(true) // Expand state

    // Filter Dialog State
    const showFilterDialog = ref(false)
    
    // Media Dialogs State
    const showDeleteConfirmDialog = ref(false)
    const showMediaDeleteConfirmDialog = ref(false)
    const editedMediaLabel = ref('')
    const editedMediaType = ref('')
    const editedMediaSource = ref('')
    const showEditLabelDialog = ref(false)
    const mediaLoading = ref(false)


    const headers = [
      { text: 'Image Name', value: 'name' },
      { text: 'Type', value: 'type' },
      { text: 'Respondents', value: 'respondents' },
      { text: 'AOIs', value: 'aois' },
      { text: 'Source', value: 'source' },
      { text: 'Actions', value: 'actions', align: 'center', sortable: false }
    ]

    const typeItems = [{text: "Shelf", value: 1}, {text: "Pack", value: 2}]
    const sourceItems = [{text: "Eyeflow", value: 1}, {text: "MET", value: 2}, {text: "Static", value: 3}]

    const exportKPI = () => {  
      if(props.project && props.project.id) {
        downloadKPIAnalysis(props.project)
      }
    }

    const openFilterDialog = () => {
      showFilterDialog.value = true
    }



    const triggerUpload = () => mediaFileInput.value.click()

    const uploadMediaFile = async (event) => {
      const file = event.target.files[0]
      if (!file) return
      uploading.value = true
      try {
        emit('add-media', file) 
      } catch (error) {
        console.error('Upload failed', error)
      } finally {
        uploading.value = false
        event.target.value = null
      }
    }
    
    const triggerFixationsFileUpload = (item) => {
      selectedItem.value = item
      fixationsFileInput.value.click();
    };

    const uploadFixationsFile = async (event) => {
      const file = event.target.files[0]
      if (!file) return
      uploading.value = true
      try {
        emit('add-fixations', selectedItem.value.id, file) 
      } catch (error) {
        console.error('Upload failed', error)
      } finally {
        uploading.value = false
        event.target.value = null 
        selectedItem.value = null
      }
    }

    const openDeleteConfirmDialog = (item) => {
      selectedItem.value = item
      showDeleteConfirmDialog.value = true;
    };
    const cancelDeletion = () => { showDeleteConfirmDialog.value = false; };
    const clearFixations = async () => {
      try {
        await emit('clear-fixations', selectedItem.value.id) 
      } catch (error) {
        console.error('Deletion failed', error)
      } finally {
        showDeleteConfirmDialog.value = false;
      }
    }

    const openEditMediaDialog = (item) => {
      selectedItem.value = item
      editedMediaLabel.value = selectedItem.value.name
      editedMediaSource.value = selectedItem.value.source
      editedMediaType.value = selectedItem.value.type
      showEditLabelDialog.value = true;
    };
    const cancelEditMedia = () => { showEditLabelDialog.value = false }
    const saveEditedMedia = async () => {
      selectedItem.value.name = editedMediaLabel.value
      selectedItem.value.source = editedMediaSource.value
      selectedItem.value.type = editedMediaType.value
      mediaLoading.value = true
      try {
        await emit('update-media', selectedItem.value) 
      } catch (error) {
        console.error('Update failed', error)  
      } finally {
        mediaLoading.value = false;
        showEditLabelDialog.value = false
      }
    }
    
    const openMediaDeleteConfirmDialog = (item) => {
      selectedItem.value = item
      showMediaDeleteConfirmDialog.value = true;
    };
    const cancelMediaDeletion = () => { showMediaDeleteConfirmDialog.value = false; };
    const clearMedia = async () => {
      try {
        await emit('clear-media', selectedItem.value.id) 
      } catch (error) {
        console.error('Deletion failed', error)
      } finally {
        showMediaDeleteConfirmDialog.value = false;
      }
    }

    const handleRowClick = (item) => {
      activeItem.value = item
      emit('selection-change', { item, selected: selected.value })
      // Auto Collapse the table when a row is clicked
      isTableExpanded.value = false 
    }

    const rowClass = (item) => {
      return item === activeItem.value ? 'selected-row' : ''
    }

    const sourceText = (sourceItem) => {
      const foundSource = sourceItems.find(item => item.value === sourceItem)
      return foundSource ? foundSource.text : 'No Source'
    }
    const typeText = (sourceItem) => {
      const foundType = typeItems.find(item => item.value === sourceItem)
      return foundType ? foundType.text : 'No Source'
    }

    watch(selected, (val) => {
       emit('selection-change', { item: activeItem.value, selected: val })
    })

    const filterCount = ref(0)

    const updateFilter = (count) => {
      selected.value = []
      handleRowClick(null)
      isTableExpanded.value = true 
      filterCount.value = count
    }

    return { 
      selected, headers, handleRowClick, rowClass, isTableExpanded,
      uploading, mediaFileInput, triggerUpload, uploadMediaFile,
      fixationsFileInput, uploadFixationsFile, triggerFixationsFileUpload,
      clearFixations, cancelDeletion, showDeleteConfirmDialog, openDeleteConfirmDialog,
      clearMedia, cancelMediaDeletion, showMediaDeleteConfirmDialog, openMediaDeleteConfirmDialog,
      editedMediaLabel, showEditLabelDialog, mediaLoading, cancelEditMedia, saveEditedMedia, openEditMediaDialog,
      editedMediaType, editedMediaSource,
      typeItems, sourceItems, sourceText, typeText, showFilterDialog, exportKPI,

      openFilterDialog, filterCount, updateFilter,
      
      icons: { 
        mdiCloudUpload, mdiProgressUpload, mdiDotsVertical,
        mdiImageOffOutline, mdiTableHeadersEye, mdiPencil,
        mdiTableHeadersEyeOff, mdiFilter, mdiFileExcelOutline,
        mdiChevronUp, mdiChevronDown
      }
     }
  }
}
</script>

<style >
.v-data-table__wrapper tbody tr{
    cursor: pointer;
}
.selected-row { background-color: #e0e0e0 !important; }
.border-right { border-right: 1px solid #e0e0e0; }
</style>